import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { useLanguage } from '../contexts/LanguageContext';

const MainWrapper = styled.div`
  background: transparent;
  color: white;
`;

const HeroSection = styled.section`
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  @media (max-width: 768px) {
    height: auto;
    min-height: 45vh;
  }
`;

const HeroContent = styled.div`
  position: absolute;
  top: 5%;
  left: 0;
  width: 100%;
  padding: 0 50px;
  box-sizing: border-box;
  z-index: 2;
  @media (max-width: 768px) {
    position: relative;
    top: auto;
    left: auto;
    transform: none;
    padding: 20px;
  }
`;

const HeroMedia = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  @media (max-width: 768px) {
    position: relative;
    height: 74vh;
    overflow: hidden;
  }
`;

const MobileHeadlineOverlay = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
    position: absolute;
    top: 1.5rem;
    left: 0;
    width: 100%;
    padding: 0 20px;
    box-sizing: border-box;
    z-index: 3;
  }
`;

const MediaItem = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: ${props => props.active ? 1 : 0};
  transform: translateX(${props => props.position}%);
  transition: opacity 0.0s ease-in-out, transform 1.5s ease-in-out;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    video {
      transform: scale(1.2);
      transform-origin: top center;
    }
  }
`;



const SubElementsSection = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SubElement = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  cursor: ${props => props.isClickable ? 'pointer' : 'default'};
  background: #040A19;
  padding: 8px;
  ${props => props.isConference && `
    outline: 10px solid #90C4D9;
    outline-offset: -2px;
  `}
`;

const SubElementImageWrapper = styled.div`
  width: 100%;
  height: 200px;
  overflow: hidden;
`;

const SubElementImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;

  ${SubElement}:hover & {
    transform: scale(1.1);
  }
`;

const SubElementContent = styled.div`
  padding: 10px 2px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ComingSoonBanner = styled.div`
  position: absolute;
  top: 30px;
  right: -50px;
  background-color: #47ABDB;
  color: white;
  padding: 5px 0;
  width: 200px;
  text-align: center;
  transform: rotate(45deg);
  font-size: 0.6em;
  font-weight: bold;
  z-index: 2;
  display: ${props => props.show ? 'block' : 'none'};
`;

const LinkTitle = styled.span`
  color: white;
  font-weight: bold;
  font-size: 1.4rem;
`;

const LinkDescription = styled.span`
  color: white;
  font-size: 1.05rem;
  line-height: 1.35;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`;

const BigHeadline = styled.h1`
  font-size: 5.5rem;
  line-height: 1.1;
  white-space: normal;
  margin-bottom: 0.5rem;
  text-shadow: 1px 1px 3px rgba(0,0,0,0.7);
  @media (max-width: 768px) {
    font-size: 2.2rem;
    text-shadow: none;
  }
`;

const ColoredWord = styled.span`
  text-shadow: 1px 1px 3px rgba(0,0,0,0.7);
  color: #48ABDB;
  @media (max-width: 768px) {
    text-shadow: none;
  }
`;

const SideText = styled.div`
  max-width: 33%;
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const SubHeadline = styled.p`
  font-size: 1.3rem;
  font-weight: bold;
  text-shadow: 1px 1px 3px rgba(0,0,0,0.7);
  color: white;
  margin-bottom: 0.7rem;
  @media (max-width: 768px) {
    font-size: 0.9rem;
    text-shadow: none;
  }
`;

const HeroParagraph = styled.p`
  font-size: 0.85rem;
  text-shadow: 1px 1px 3px rgba(0,0,0,0.7);
  color: white;
  opacity: 0.92;
  margin-bottom: 0.35rem;
  line-height: 1.4;
  @media (max-width: 768px) {
    font-size: 0.8rem;
    text-shadow: none;
  }
`;

const HeroQuote = styled.blockquote`
  position: absolute;
  bottom: 4%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  width: 70%;
  text-align: center;
  font-size: 1rem;
  font-style: italic;
  text-shadow: 1px 1px 3px rgba(0,0,0,0.7);
  color: white;
  opacity: 0.85;
  line-height: 1.5;
  margin: 0;
  @media (max-width: 768px) {
    position: relative;
    bottom: auto;
    left: auto;
    transform: none;
    width: 100%;
    max-width: 100%;
    font-size: 0.9rem;
    margin-top: 1rem;
    text-shadow: none;
  }
`;

const ScienceCares = styled.h2`
  color: #48ABDB;
  text-shadow: 1px 1px 3px rgba(0,0,0,0.7);
  margin-bottom: 0.6rem;
  @media (max-width: 768px) {
    text-shadow: none;
  }
`;

const ScienceCaresFirstWord = styled.span`
  font-size: 1rem;
  display: block;
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ScienceCaresRest = styled.span`
  font-size: 1.8rem;
  display: block;
  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const LogoOverlay = styled.div`
  position: absolute;
  top: 58%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  width: max(42vw, 34vh);

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 170%;
    height: 170%;
    background: radial-gradient(circle, rgba(255,0,0,0.3) 0%, rgba(0,0,0,0) 70%);
    z-index: -1;
  }
  
  img {
    width: 100%;
    height: auto;
    display: block;
  }

  @media (max-width: 768px) {
    top: 58%;
    width: 72vw;
  }
`;

const WelcomeSection = styled.section`
  width: 100%;
  background: white;
  padding: 1.5rem 0;
`;

const WelcomeContent = styled.div`
  padding-left: 50px; // Match HeroContent's left position
  text-align: left;
  
  @media (max-width: 768px) {
    padding: 0 20px; // Match HeroContent's mobile padding
  }
`;

const WelcomeSmall = styled.div`
  color: #48ABDB;
  font-size: 1.5rem;
  margin-bottom: 0.2rem;
`;

const WelcomeLarge = styled.div`
  color: #48ABDB;
  font-size: 5.5rem;
  font-weight: bold;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const SlideShowContent = () => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const videoRefs = useRef([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { language } = useLanguage();

  const slideShowItems = [
    {
      id: 1,
      mediaType: 'VIDEO',
      mediaUrl: '/videos/background.mp4'
    }
  ];

  useEffect(() => {
    // Log when the video loads or errors
    const videoElement = videoRefs.current[0];
    if (videoElement) {
      videoElement.addEventListener('loadeddata', () => {
        console.log('Video loaded successfully');
      });
      
      videoElement.addEventListener('error', (e) => {
        console.error('Error loading video:', videoElement.error);
      });
    }
  }, []);

  const subElements = [
    {
      title: "HEARTS CONFERENCES",
      description: "Conferences on the medical arts for doctors and health professionals.",
      url: "/images/hearts_conference.png",
      externalUrl: "https://conference.heartport.org/",
      showComingSoon: false
    },
    {
      title: "HEARTPORT PUBLIC",
      description: "Build your best self with health science.",
      url: "/images/heartport_public.jpg",
      externalUrl: "https://public.heartport.org/",
      showComingSoon: false
    },
    {
      title: "HEARTPORT EXPERT",
      description: "Latest research, simply summarized for busy doctors.",
      url: "/images/heartport_expert.jpg",
      externalUrl: "https://expert.heartport.org/",
      showComingSoon: false
    }
  ];

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (slideShowItems.length > 1) {
      const interval = setInterval(() => {
        setIsTransitioning(true);
        setTimeout(() => {
          setCurrentSlideIndex((prevIndex) => (prevIndex + 1) % slideShowItems.length);
          setIsTransitioning(false);
        }, 1500);
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [slideShowItems.length]);

  const getSlidePosition = (index) => {
    if (index === currentSlideIndex) return isTransitioning ? -100 : 0;
    const nextIndex = (currentSlideIndex + 1) % slideShowItems.length;
    if (index === nextIndex) return isTransitioning ? 0 : 100;
    return 100;
  };

  const handleSubElementClick = (element) => {
    if (element.externalUrl && !element.showComingSoon) {
      window.open(element.externalUrl, '_blank');
    }
  };


  return (
    <MainWrapper>
      <WelcomeSection>
        <WelcomeContent>
          <WelcomeSmall>Welcome at</WelcomeSmall>
          <WelcomeLarge>HEARTPORT</WelcomeLarge>
        </WelcomeContent>
      </WelcomeSection>
      <HeroSection>
        <HeroMedia>
          {slideShowItems.map((slide, index) => (
            <MediaItem
              key={slide.id}
              active={index === currentSlideIndex}
              position={getSlidePosition(index)}
            >
              {slide.mediaType === 'VIDEO' ? (
                <video
                  ref={el => videoRefs.current[index] = el}
                  playsInline
                  muted
                  autoPlay
                  loop
                  style={{ backgroundColor: '#000' }} // Add this to help see video boundaries
                >
                  <source 
                    src={slide.mediaUrl} 
                    type="video/mp4"
                    onError={(e) => console.error('Error loading video source:', e)}
                  />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img src={slide.mediaUrl} alt="" />
              )}
            </MediaItem>
          ))}
          {isMobile && (
            <MobileHeadlineOverlay>
              <BigHeadline>
                <ColoredWord>DEMOCRATIZING SCIENCE</ColoredWord>
              </BigHeadline>
              <SubHeadline>
                HEARTPORT is your portal for maximizing life through the Art of Medical Science.
              </SubHeadline>
            </MobileHeadlineOverlay>
          )}
          <LogoOverlay>
            <img src="/images/hero_logo.png" alt="Hero Logo" />
          </LogoOverlay>
        </HeroMedia>
        <HeroContent>
          {!isMobile && (
            <BigHeadline>
              <ColoredWord>DEMOCRATIZING SCIENCE</ColoredWord>
            </BigHeadline>
          )}
          {!isMobile && (
            <SubHeadline>
              HEARTPORT is your portal for maximizing life through the Art of Medical Science.
            </SubHeadline>
          )}
          <SideText>
            <HeroParagraph>
              We make science actionable and transformative at our conferences and across our digital platforms. For Doctors. For the Public.
            </HeroParagraph>
            <HeroParagraph>
              Medicine and Art have one thing in common:<br />They refuse the limits of nature – to go beyond.
            </HeroParagraph>
            <HeroParagraph>
              We are medicine through communication.
            </HeroParagraph>
            <ScienceCares>
              <ScienceCaresFirstWord>Because at HEARTPORT,</ScienceCaresFirstWord>
              <ScienceCaresRest>SCIENCE CARES.</ScienceCaresRest>
            </ScienceCares>
          </SideText>
        </HeroContent>
        <HeroQuote>
          "Health is a state of complete physical, mental and social well-being and not merely the absence of disease or infirmity." — Constitution WHO
        </HeroQuote>
      </HeroSection>
      <SubElementsSection>
        {subElements.map((element, index) => (
          <SubElement
            key={index}
            isConference={element.isConference}
            isClickable={!element.showComingSoon && element.externalUrl}
            onClick={() => handleSubElementClick(element)}
          >
            <SubElementImageWrapper>
              <SubElementImage src={element.url} alt={element.title} />
            </SubElementImageWrapper>
            <SubElementContent>
              <LinkTitle>{element.title}</LinkTitle>
              <LinkDescription>{element.description}</LinkDescription>
            </SubElementContent>
            <ComingSoonBanner show={element.showComingSoon}>
              COMING SOON
            </ComingSoonBanner>
          </SubElement>
        ))}
      </SubElementsSection>
    </MainWrapper>
  );
};


export default SlideShowContent;
